/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Page Tariffs And Services Title Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author ILya Babanov
 * @version 1.22.0
 * @since 1.4.0
 */

import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import { PageTitle, Select } from '@components/common';
import { FlexMiddleStyled } from '@components/common/Block/Flexbox';
import { LayoutStyled } from '@components/common/Block/Layout';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageProducts,
} from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import type { IRootReducer } from '@reducers/rootReducer';

import { ExpensesAnalyzerChartTabStyled } from '../../PageExpensesAnalyzer/components/ExpensesAnalyzerController/ExpensesAnalyzerChartTab/styled/ExpensesAnalyzerChartStyled';
import { productsSetTabSelected, setTariffsAndServicesPaymentSelected } from '../tariffsAndServicesActions';
import { EProductsPayment, EProductsTab, formProductsPaymentOptions } from '../tariffsAndServicesConstants';
import {
    makeSelectProductsCategorySelected,
    makeSelectProductsFilterRegionVisible,
    makeSelectProductsMeta,
    makeSelectProductsPaymentSelected,
    makeSelectProductsTabSelected,
} from '../tariffsAndServicesSelectors';
import type {
    IProductsMeta,
    IProductsSetTabSelectedPayload,
    ISetTariffsAndServicesPaymentSelectedPayload,
} from '../tariffsAndServicesTypes';
import ProductsFilterRegion from './ProductsFilterRegion';
import { TariffsAndServicesTitlePanelStyled } from './styled/TariffsAndServicesTitleStyled';
import {
    TariffsAndServicesFilterContainerStyled,
    TariffsAndServicesFilterPaymentStyled,
} from './styled/TarriffsAndServicesFilterStyled';

export type TPageTariffsAndServicesTitle = IConnectedProps & IDispatchProps;

export const PageProductsTitle = (props: TPageTariffsAndServicesTitle) => {
    const {
        categorySelected,
        productsMeta,
        tabSelected,
    } = props;

    const [isPaymentFilterVisible, setPaymentFilterVisible] = useState(false);

    useEffect(() => {
        setPaymentFilterVisible(tabSelected === EProductsTab.AVAILABLE
            && categorySelected !== productsMeta.tariffsCategoryId
            && categorySelected !== productsMeta.campaignServicesCategoryId
            && categorySelected !== productsMeta.campaignTariffsCategoryId);
    }, [tabSelected, categorySelected, productsMeta]);

    const filterPaymentOptions = useMemo(() =>
            formProductsPaymentOptions(props.tariffsAndServicesTranslation),
        [props.tariffsAndServicesTranslation]);

    const paymentSelectedOption = useMemo(() =>
            filterPaymentOptions.find(item => item.value === props.paymentSelected),
        [filterPaymentOptions, props.paymentSelected]);

    return <Fragment>

        <LayoutStyled>
            <PageTitle dataName="tariffs-and-services-title" title={props.tariffsAndServicesTranslation.header.title}/>
        </LayoutStyled>

        <TariffsAndServicesTitlePanelStyled>

            {/* HEADER LEFT */}
            <div {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.productTabSelected }} data-name='header-left'>
                <FlexMiddleStyled>
                    <ExpensesAnalyzerChartTabStyled
                        data-name='chart-tab-available'
                        isActive={props.tabSelected === EProductsTab.AVAILABLE}
                        onClick={() => {
                            props.setTabSelected({ tabSelected: EProductsTab.AVAILABLE });
                        }}>
                        {props.tariffsAndServicesTranslation.filters.types.available}
                    </ExpensesAnalyzerChartTabStyled>
                    <ExpensesAnalyzerChartTabStyled
                        data-name='chart-tab-connected'
                        isActive={props.tabSelected === EProductsTab.CONNECTED}
                        onClick={() => {
                            props.setTabSelected({ tabSelected: EProductsTab.CONNECTED });
                        }}>
                        {props.tariffsAndServicesTranslation.filters.types.connected}
                    </ExpensesAnalyzerChartTabStyled>
                </FlexMiddleStyled>
            </div>

            {/* HEADER RIGHT */}
            <TariffsAndServicesFilterContainerStyled data-name='header-right'>
                {
                    props.isFilterRegionVisible &&
                    <ProductsFilterRegion/>
                }
                {
                    isPaymentFilterVisible &&
                    <TariffsAndServicesFilterPaymentStyled>
                        <Select
                            icon={icons.arrowListDownSmall}
                            kind="normal"
                            options={filterPaymentOptions}
                            placeholder={''}
                            selectedOption={paymentSelectedOption}
                            onSelect={item => props.setPaymentSelected({
                                paymentSelected: item.value as EProductsPayment,
                            })}
                        />
                    </TariffsAndServicesFilterPaymentStyled>
                }
            </TariffsAndServicesFilterContainerStyled>
        </TariffsAndServicesTitlePanelStyled>

    </Fragment>;
};

interface IConnectedProps {
    categorySelected: number;
    commonTranslation: ICommonTranslation;
    isFilterRegionVisible: boolean;
    paymentSelected: EProductsPayment;
    productsMeta: IProductsMeta;
    tabSelected: EProductsTab;
    tariffsAndServicesTranslation: IPageProductsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    categorySelected: makeSelectProductsCategorySelected(),
    commonTranslation: makeSelectTranslationCommon(),
    isFilterRegionVisible: makeSelectProductsFilterRegionVisible(),
    paymentSelected: makeSelectProductsPaymentSelected(),
    productsMeta: makeSelectProductsMeta(),
    tabSelected: makeSelectProductsTabSelected(),
    tariffsAndServicesTranslation: makeSelectTranslationPageProducts(),
});

interface IDispatchProps {
    setPaymentSelected: (payload: ISetTariffsAndServicesPaymentSelectedPayload) => void;
    setTabSelected: (payload: IProductsSetTabSelectedPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setPaymentSelected: payload => dispatch(setTariffsAndServicesPaymentSelected(payload)),
    setTabSelected: payload => dispatch(productsSetTabSelected(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PageProductsTitle);
